
"use client";

import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import type { User } from 'firebase/auth';
import { onIdTokenChanged, signOut } from 'firebase/auth';
import { auth, db } from '@/lib/firebase';
import { Skeleton } from '../ui/skeleton';
import { doc, onSnapshot } from 'firebase/firestore';
import type { UserProfile } from '@/types';

type AuthContextType = {
  user: User | null;
  userProfile: UserProfile | null;
  loading: boolean;
};

const AuthContext = createContext<AuthContextType>({ user: null, userProfile: null, loading: true });


export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribeAuth = onIdTokenChanged(auth, async (authUser) => {
      if (authUser) {
        const idToken = await authUser.getIdToken();
        
        // This is the new part: send the token to the server to create a session cookie.
        try {
          const response = await fetch('/api/auth/session', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ idToken }),
          });
          if (!response.ok) {
            throw new Error('Session creation failed.');
          }
          setUser(authUser);
        } catch (error) {
          console.error("Failed to create session:", error);
          if (process.env.NODE_ENV === 'development') {
            console.warn("Continuing with client-side Firebase auth in development without a server session cookie.");
            setUser(authUser);
            return;
          }
          setUser(null);
          setUserProfile(null);
          setLoading(false);
          // If session creation fails, sign out to prevent inconsistent states.
          await signOut(auth);
        }

      } else {
        setUser(null);
        setUserProfile(null);
        setLoading(false);
      }
    });

    return () => unsubscribeAuth();
  }, []);

  useEffect(() => {
    if (!user) {
      // Handled by the auth state listener setting loading to false.
      return;
    }

    const userDocRef = doc(db, 'users', user.uid);
    const unsubscribeProfile = onSnapshot(userDocRef, (docSnap) => {
        if (docSnap.exists()) {
            const userData = docSnap.data();
            setUserProfile({
                id: docSnap.id,
                ...userData,
            } as UserProfile);
        } else {
            setUserProfile(null);
        }
        setLoading(false);
    }, (error) => {
        console.error("Error fetching user profile:", error);
        setUserProfile(null);
        setLoading(false);
    });
      
    return () => unsubscribeProfile();
  }, [user]);


  return (
    <AuthContext.Provider value={{ user, userProfile, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    const userRoles = context.userProfile?.roles || [];
    return { ...context, userRoles };
};
